import { useEffect, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCr } from "@/lib/format";
import { toast } from "sonner";

/**
 * Right-side drill-down panel for a single ledger line item — pulls the
 * per-month sub-item breakdown from /api/cashflow/line-item?key=…
 */
export default function LineItemDrillPanel({ rowKey, horizon, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!rowKey) return;
    setLoading(true);
    api
      .get("/cashflow/line-item", { params: { key: rowKey, horizon } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) }))
      .finally(() => setLoading(false));
  }, [rowKey, horizon]);

  if (!rowKey) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="cf-lineitem-backdrop"
      />
      <aside
        data-testid="cf-lineitem-panel"
        className="fixed top-0 right-0 z-50 h-full w-full sm:w-[720px] shadow-2xl overflow-y-auto cf-fade-up"
        style={{ background: "var(--cf-surface)" }}
      >
        <header
          className="sticky top-0 z-10 px-5 py-4 border-b border-hairline flex items-center justify-between"
          style={{ background: "var(--cf-surface)" }}
        >
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">
              Line-item drill-down
            </div>
            <h3 className="font-display text-xl text-ink font-bold truncate">
              {data?.label || rowKey}
            </h3>
            {data?.source && (
              <div className="text-[10px] text-muted-dark mt-0.5">
                Source: <span className="font-mono">{data.source}</span>
                {data.items?.length ? ` · ${data.items.length} items` : ""}
              </div>
            )}
          </div>
          <button
            data-testid="cf-lineitem-close"
            onClick={onClose}
            className="text-muted-dark hover:text-ink"
          >
            <X size={20} />
          </button>
        </header>

        {loading && <div className="p-8 text-center text-sm text-muted-dark">Loading…</div>}
        {!loading && data && (
          <div className="p-5 space-y-5">
            {/* Total strip */}
            <div className="cf-card p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold">
                  Aggregate (horizon)
                </div>
                <div className="text-base font-display font-bold text-ink num tabular-nums">
                  {formatCr(data.total)}
                </div>
              </div>
              <ArrowRight size={16} className="text-muted-light" />
              <div className="text-[11px] text-muted-dark">
                {data.month_labels?.[0]} → {data.month_labels?.[data.month_labels.length - 1]}
              </div>
            </div>

            {data.items?.length === 0 && (
              <div className="cf-card p-6 text-center text-sm text-muted-dark">
                No sub-item detail available for this row.
              </div>
            )}

            {data.items?.length > 0 && (
              <div className="cf-card p-0 overflow-hidden">
                <div className="overflow-x-auto max-h-[70vh]">
                  <table className="min-w-full text-[11px] border-collapse">
                    <thead className="sticky top-0 z-10" style={{ background: "var(--cf-surface-2)" }}>
                      <tr>
                        <th className="text-left px-3 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold sticky left-0 z-20 border-r border-hairline min-w-[180px]" style={{ background: "var(--cf-surface-2)" }}>
                          Sub-item
                        </th>
                        {data.month_labels?.map((lbl) => (
                          <th
                            key={lbl}
                            className="text-right px-2.5 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold whitespace-nowrap"
                          >
                            {lbl}
                          </th>
                        ))}
                        <th className="text-right px-3 py-2 uppercase tracking-widest text-[9.5px] text-muted-dark font-semibold sticky right-0 z-20 border-l border-hairline" style={{ background: "var(--cf-surface-2)" }}>
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((it) => (
                        <tr
                          key={it.name}
                          className="border-t border-hairline/40 hover:bg-surface-2/40"
                          data-testid={`cf-lineitem-row-${it.name}`}
                        >
                          <td className="px-3 py-1.5 text-ink sticky left-0 z-10 border-r border-hairline/40 whitespace-nowrap min-w-[180px]" style={{ background: "var(--cf-surface)" }}>
                            <div className="font-medium text-ink">{it.name}</div>
                            {it.category && (
                              <div className="text-[9.5px] text-muted-light uppercase tracking-widest">
                                {it.category}
                              </div>
                            )}
                          </td>
                          {data.months.map((m) => {
                            const v = it.values?.[m] || 0;
                            return (
                              <td
                                key={m}
                                className="text-right px-2.5 py-1.5 num tabular-nums whitespace-nowrap"
                              >
                                <MoneyCell v={v} />
                              </td>
                            );
                          })}
                          <td className="text-right px-3 py-1.5 num tabular-nums whitespace-nowrap sticky right-0 z-10 border-l border-hairline/40 font-bold text-ink" style={{ background: "var(--cf-surface)" }}>
                            {formatCr(it.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-hairline" style={{ background: "var(--cf-surface-2)" }}>
                        <td className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-dark font-bold sticky left-0 z-10 border-r border-hairline" style={{ background: "var(--cf-surface-2)" }}>
                          Total
                        </td>
                        {data.months.map((m) => {
                          const sum = data.items.reduce((a, it) => a + (it.values?.[m] || 0), 0);
                          return (
                            <td key={m} className="text-right px-2.5 py-2 num tabular-nums whitespace-nowrap font-bold text-ink">
                              <MoneyCell v={sum} />
                            </td>
                          );
                        })}
                        <td className="text-right px-3 py-2 num tabular-nums whitespace-nowrap sticky right-0 z-10 border-l border-hairline font-bold text-ink" style={{ background: "var(--cf-surface-2)" }}>
                          {formatCr(data.total)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function MoneyCell({ v }) {
  if (!v || Math.abs(v) < 0.005) return <span className="text-muted-light">–</span>;
  const neg = v < 0;
  return (
    <span className={neg ? "text-red-500" : "text-ink"}>
      {neg ? "−" : ""}
      {formatCr(Math.abs(v))}
    </span>
  );
}
